import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, MessageCircle, ArrowRight, RefreshCw, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * ChatRecoveryPanel — admin tool for restoring orphaned chat rooms.
 *
 * An "orphaned" room is a chat room whose ID doesn't match any current
 * `books.id`. This usually happens when a book was deleted-and-re-uploaded
 * (gets a fresh UUID), leaving historical messages stranded under the old
 * book_id. The panel lists every orphan and lets the admin map each one to
 * a current book in one click.
 */
const ChatRecoveryPanel = ({ books = [] }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [selections, setSelections] = useState({});  // {room: targetBookId}
  const [rebinding, setRebinding] = useState(null);   // currently-rebinding room id
  const [deleting, setDeleting] = useState(null);
  const token = localStorage.getItem('token');

  const fetchOrphans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chat/admin/orphaned-rooms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data?.orphans || [];
      setOrphans(list);
      // Pre-fill suggested targets
      const next = {};
      list.forEach((o) => { if (o.suggested_target_book_id) next[o.room] = o.suggested_target_book_id; });
      setSelections(next);
    } catch (e) {
      toast({
        title: 'Could not load orphaned chat rooms',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrphans(); /* eslint-disable-next-line */ }, []);

  const rebind = async (orphan) => {
    const target = selections[orphan.room];
    if (!target) {
      toast({ title: 'Pick a target book first', variant: 'destructive' });
      return;
    }
    setRebinding(orphan.room);
    try {
      const res = await axios.post(
        `${API}/chat/admin/rebind-room`,
        { from_room: orphan.room, to_room: target },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: `Restored ${res.data?.moved ?? 0} message${res.data?.moved === 1 ? '' : 's'}`,
        description: `Now visible in the discussion for "${(books.find((b) => b.id === target) || {}).title || target}"`,
      });
      await fetchOrphans();
    } catch (e) {
      toast({
        title: 'Could not rebind chat room',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setRebinding(null);
    }
  };

  const purge = async (orphan) => {
    const n = orphan.message_count;
    if (!window.confirm(`Permanently delete ${n} stranded message${n === 1 ? '' : 's'} from room ${orphan.room}? This cannot be undone.`)) return;
    setDeleting(orphan.room);
    try {
      const res = await axios.delete(`${API}/chat/admin/orphaned-rooms/${encodeURIComponent(orphan.room)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: `Deleted ${res.data?.deleted ?? 0} message${res.data?.deleted === 1 ? '' : 's'}`, description: 'The orphaned room was permanently removed.' });
      setOrphans((prev) => prev.filter((o) => o.room !== orphan.room));
    } catch (e) {
      toast({ title: 'Could not delete chat room', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden" data-testid="chat-recovery-panel">
      <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-fuchsia-400" />
            Chat Recovery
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Restore book discussion messages stranded under old book IDs (e.g. after a book was deleted and re-uploaded).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchOrphans}
          disabled={loading}
          className="bg-white/5 border-white/10 text-white hover:bg-white/10"
          data-testid="chat-recovery-refresh"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Re-scan
        </Button>
      </div>

      {loading && orphans.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">Scanning chat rooms…</div>
      ) : orphans.length === 0 ? (
        <div className="p-8 text-center text-emerald-400 text-sm" data-testid="chat-recovery-empty">
          ✓ No orphaned chat rooms — every discussion is correctly linked to its book.
        </div>
      ) : (
        <ul className="divide-y divide-white/10">
          {orphans.map((o) => (
            <li key={o.room} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`chat-orphan-${o.room}`}>
              <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs text-amber-300 truncate">{o.room}</div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {o.message_count} message{o.message_count === 1 ? '' : 's'}
                  {o.last_at && <span> · last {new Date(o.last_at).toLocaleDateString()}</span>}
                  {o.sample_authors?.length > 0 && (
                    <span> · {o.sample_authors.slice(0, 3).join(', ')}</span>
                  )}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <select
                value={selections[o.room] || ''}
                onChange={(e) => setSelections((s) => ({ ...s, [o.room]: e.target.value }))}
                className="bg-zinc-900 border border-white/10 rounded-md text-white text-xs px-2 py-1.5 max-w-[260px]"
                data-testid={`chat-orphan-target-${o.room}`}
              >
                <option value="">— choose target book —</option>
                {books.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title}{o.suggested_target_book_id === b.id ? '  ★ suggested' : ''}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={() => rebind(o)}
                disabled={!selections[o.room] || rebinding === o.room}
                className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
                data-testid={`chat-orphan-rebind-${o.room}`}
              >
                {rebinding === o.room ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Restoring…</>
                ) : (
                  'Restore'
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => purge(o)}
                disabled={deleting === o.room || rebinding === o.room}
                className="border-red-500/40 text-red-400 hover:bg-red-500/15 hover:text-red-300"
                title="Permanently delete these stranded messages"
                data-testid={`chat-orphan-delete-${o.room}`}
              >
                {deleting === o.room ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ChatRecoveryPanel;
